import React, { useState, useEffect } from 'react';
import Layout from '../component/layout'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { makeRequest2 } from '../../../utils/makeRequest';
import { getItem } from '../../../utils/localStorage';
import {
    getCustomerURL,
    getCustomerUnits,
    getLandlordUnpaidInvoices,
    deactivateLandlordURL
} from '../../../utils/urls';
import { toastify } from '../../../utils/toast';

const LandlordDeactivation = () => {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState(null);
    const [units, setUnits] = useState([]);
    const [selectedUnits, setSelectedUnits] = useState([]);

    const [unpaidInvoices, setUnpaidInvoices] = useState([]);
    const [invoiceSummary, setInvoiceSummary] = useState(null);
    const [loadingInvoices, setLoadingInvoices] = useState(false);


    useEffect(() => {
        fetchCustomerAndUnits();
    }, [customerId]);

    const fetchCustomerAndUnits = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');

            // Fetch customer details
            const customerResponse = await makeRequest2(
                `${getCustomerURL}/${customerId}`,
                'GET',
                {}
            );

            // Fetch units
            const unitsResponse = await makeRequest2(
                `${getCustomerUnits}/${facilityId}/${customerId}`,
                'GET',
                {}
            );

            if (customerResponse.success) {
                setCustomer(customerResponse.data);
            }

            if (unitsResponse.success) {
                setUnits(unitsResponse.data);
                // Pre-select all units by default
                setSelectedUnits(unitsResponse.data.map(unit => unit._id));
            }
        } catch (error) {
            toastify('Error fetching data: ' + error.message);
        }
    };

    const handleUnitSelection = (unitId) => {
        setSelectedUnits(prev =>
            prev.includes(unitId)
                ? prev.filter(id => id !== unitId)
                : [...prev, unitId]
        );
    };

    const fetchUnpaidInvoices = async () => {
        try {
            setLoadingInvoices(true);
            const facilityId = await getItem('selectedFacilityId');

            const response = await makeRequest2(
                `${getLandlordUnpaidInvoices}/${facilityId}/${customerId}`,
                'GET',
                {}
            );

            console.log("Invoices Response:", response);

            if (response.success) {
                setUnpaidInvoices(response.data.invoices);
                setInvoiceSummary(response.data.summary);
            }
        } catch (error) {
            toastify('Error fetching unpaid invoices: ' + error.message);
        } finally {
            setLoadingInvoices(false);
        }
    };


    const handleDeactivation = async () => {
        if (selectedUnits.length === 0) {
            toastify('Please select at least one unit to remove', 'error');
            return;
        }

        // Check for unpaid invoices
        // if (unpaidInvoices.length > 0) {
        //     toastify('Cannot deactivate landlord with unpaid invoices. Please clear all outstanding balances first.', 'error');
        //     return;
        // }

        // Confirmation dialog
        const isRemovingAll = selectedUnits.length === units.length;
        const confirmMessage = isRemovingAll
            ? 'This will completely deactivate the landlord and remove them from all units. Continue?'
            : `This will remove the landlord from ${selectedUnits.length} unit(s). Continue?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            const facilityId = await getItem('selectedFacilityId');

            const response = await makeRequest2(
                `${deactivateLandlordURL}/${facilityId}`,
                'POST',
                {
                    customerId: customerId,
                    unitIds: selectedUnits
                }
            );

            console.log("Deactivation Response:", response);

            if (response.success) {
                toastify(response.data.message, 'success');

                navigate('/facility/customer_management/customers');
            } else {
                toastify(response.error || 'Failed to deactivate landlord', 'error');
            }

        } catch (error) {
            toastify('Error: ' + error.message, 'error');
        }
    };


    useEffect(() => {
        fetchUnpaidInvoices();
    }, [])

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"/facility/"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"#"}>Landlord Deactivation</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <Link to={"#"} onClick={() => navigate(-1)}><span><i className="ti ti-arrow-narrow-left"></i> Back</span></Link>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-header">
                                    <h5>Landlord Deactivation</h5>
                                </div>

                                <div className="card-body">
                                    {/* Customer Details */}
                                    {customer && (
                                        <div className="mb-4">
                                            <h5 className="border-bottom pb-2">Landlord Details</h5>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <p><strong>Name:</strong> {customer.firstName + '' + customer.lastName}</p>
                                                    <p><strong>Email:</strong> {customer.email}</p>
                                                </div>
                                                <div className="col-md-6">
                                                    <p><strong>Phone:</strong> {customer.phoneNumber}</p>
                                                    <p><strong>Status:</strong> {customer.status}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Units Section */}
                                    <div className="mb-4">
                                        <h5 className="border-bottom pb-2">
                                            Units ({units.length})
                                            {units.length > 1 && (
                                                <span className="text-muted small ms-2">
                                                    - Select units to remove landlord from
                                                </span>
                                            )}
                                        </h5>

                                        {units.length === 0 ? (
                                            <p className="text-muted">No units found</p>
                                        ) : (
                                            <div className="row">
                                                {units.map(unit => (
                                                    <div key={unit._id} className="col-md-6 mb-3">
                                                        <div className={`card ${selectedUnits.includes(unit._id) ? 'border-danger' : ''}`}>
                                                            <div className="card-body">
                                                                {units.length > 1 && (
                                                                    <div className="form-check mb-2">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            checked={selectedUnits.includes(unit._id)}
                                                                            onChange={() => handleUnitSelection(unit._id)}
                                                                            id={`unit-${unit._id}`}
                                                                        />
                                                                        <label className="form-check-label" htmlFor={`unit-${unit._id}`}>
                                                                            <strong>Select to remove</strong>
                                                                        </label>
                                                                    </div>
                                                                )}

                                                                <h6>{unit.name}</h6>
                                                                <p className="mb-1"><strong>Type:</strong> {unit.unitType}</p>
                                                                <p className="mb-1"><strong>Floor:</strong> {unit.floorUnitNo}</p>
                                                                <p className="mb-1"><strong>Division:</strong> {unit.division}</p>
                                                                <p className="mb-0"><strong>Status:</strong> {unit.status}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Unpaid Invoices Section */}
                                    <div className="mb-4">
                                        <h5 className="border-bottom pb-2">
                                            Unpaid Invoices
                                        </h5>

                                        {loadingInvoices ? (
                                            <p className="text-muted">Loading invoices...</p>
                                        ) : unpaidInvoices.length === 0 ? (
                                            <div className="alert alert-success">
                                                ✅ No unpaid invoices found. Landlord can be deactivated.
                                            </div>
                                        ) : (
                                            <>
                                                <div className="alert alert-danger">
                                                    ⚠️ This landlord has outstanding invoices and cannot be deactivated.
                                                </div>

                                                <div className="table-responsive">
                                                    <table className="table table-bordered table-sm">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th>Invoice #</th>
                                                                <th>Unit</th>
                                                                <th>Issue Date</th>
                                                                <th>Due Date</th>
                                                                <th>Total</th>
                                                                <th>Paid</th>
                                                                <th>Balance</th>
                                                                <th>Status</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {unpaidInvoices.map(inv => (
                                                                <tr key={inv._id}>
                                                                    <td>{inv.invoiceNumber}</td>
                                                                    <td>{inv.unitName}</td>
                                                                    <td>{new Date(inv.issueDate).toLocaleDateString()}</td>
                                                                    <td>{new Date(inv.dueDate).toLocaleDateString()}</td>
                                                                    <td>{inv.totalAmount}</td>
                                                                    <td>{inv.amountPaid}</td>
                                                                    <td className="text-danger fw-bold">
                                                                        {inv.balance}
                                                                    </td>
                                                                    <td>
                                                                        <span className="badge bg-warning">
                                                                            {inv.status}
                                                                        </span>
                                                                    </td>
                                                                    {/* <td>
                                                                        <a
                                                                            href={inv.invoiceUrl}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            className="btn btn-sm btn-outline-primary"
                                                                        >
                                                                            View
                                                                        </a>
                                                                    </td> */}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {invoiceSummary && (
                                                    <div className="alert alert-warning mt-3">
                                                        <strong>Total Outstanding Balance:</strong> {invoiceSummary.totalBalance}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>


                                    {/* Action Message */}
                                    {units.length > 0 && (
                                        <div className="alert alert-warning">
                                            {selectedUnits.length === units.length ? (
                                                <strong>⚠️ All units selected - This will completely deactivate the landlord</strong>
                                            ) : (
                                                <strong>📝 {selectedUnits.length} of {units.length} units selected - Landlord will be removed from selected units only</strong>
                                            )}
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="d-flex gap-3 justify-content-end">
                                        <button
                                            className="btn btn-secondary px-4"
                                            onClick={() => navigate(-1)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="btn btn-danger px-4"
                                            onClick={handleDeactivation}
                                            // disabled={selectedUnits.length === 0 || unpaidInvoices.length > 0}
                                        >
                                            {selectedUnits.length === units.length
                                                ? 'Deactivate Landlord'
                                                : `Remove from ${selectedUnits.length} Unit(s)`
                                            }
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </Layout>
    );
};

export default LandlordDeactivation;